import { Auth0Provider } from "@auth0/auth0-react";
import type { ReactNode } from "react";

import { PRODUCT_HOME } from "@/lib/product";

const DEFAULT_AUTH0_DOMAIN = "dev-nzzxr2nzimekjvoi.uk.auth0.com";
const DEFAULT_AUTH0_CLIENT_ID = "r40iK0A5z2xPJ5tpEAEkUqsu6STvNu1E";
const DEFAULT_AUTH0_PRIMARY_ORIGIN = "https://wallet.genrl.co";
const DEFAULT_AUTH0_COMPUTE_ORIGIN = "https://compute.genrl.co";
const DEFAULT_AUTH0_RELAY_ORIGIN = "https://new-fe-xi.vercel.app";
const LEGACY_AUTH0_RELAY_ORIGIN = "https://new-fe-xi.vercel.app";
const AUTH0_LOGGED_OUT_KEY = "genrl.auth0.logged_out";
const AUTH0_PENDING_KEY = "genrl.auth0.pending";
const AUTH0_PENDING_COOKIE = "genrl_auth0_pending";
const AUTH0_CONSUMED_CALLBACK_KEY = "genrl.auth0.callback_consumed";
const AUTH0_ERROR_KEY = "genrl.auth0.error";
const AUTH0_RETURN_TO_KEY = "genrl.auth0.return_to";
const AUTH0_SWITCH_FROM_EMAIL_KEY = "genrl.auth0.switch_from_email";
const AUTH0_PENDING_TTL_MS = 10 * 60 * 1000;
let lastConsumedHostedAuth0Callback = "";

type Auth0StorageKind = "localStorage" | "sessionStorage";

function readAuth0Storage(kind: Auth0StorageKind, key: string): string {
  if (typeof window === "undefined") return "";
  try {
    return String(window[kind].getItem(key) || "");
  } catch {
    return "";
  }
}

function writeAuth0Storage(kind: Auth0StorageKind, key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    window[kind].setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeAuth0Storage(kind: Auth0StorageKind, key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    window[kind].removeItem(key);
    return true;
  } catch {
    return false;
  }
}

type PendingHostedAuth0 = {
  state: string;
  nonce: string;
  startedAt: number;
  returnTo?: string;
  expectedEmail?: string;
  disallowedEmail?: string;
};

export type HostedAuth0RedirectOptions = {
  screenHint?: "signup";
  connection?: "google-oauth2";
  returnTo?: string;
  prompt?: string;
  loginHint?: string;
  expectedEmail?: string;
  disallowedEmail?: string;
  maxAge?: number;
  forceAccountSelection?: boolean;
};

export function auth0Domain(): string {
  return String(import.meta.env.VITE_AUTH0_DOMAIN || DEFAULT_AUTH0_DOMAIN).trim();
}

export function auth0ClientID(): string {
  return String(import.meta.env.VITE_AUTH0_CLIENT_ID || DEFAULT_AUTH0_CLIENT_ID).trim();
}

export function auth0Enabled(): boolean {
  const forced = String(import.meta.env.VITE_AUTH0_ENABLED ?? "").trim().toLowerCase();
  if (forced === "0" || forced === "false" || forced === "off") return false;
  return !!auth0Domain() && !!auth0ClientID();
}

function normalizeAuth0Origin(value: string): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    return new URL(raw).origin;
  } catch {
    return "";
  }
}

function auth0PrimaryOrigin(): string {
  return normalizeAuth0Origin(String(import.meta.env.VITE_AUTH0_PRIMARY_ORIGIN || DEFAULT_AUTH0_PRIMARY_ORIGIN));
}

function auth0ComputeOrigin(): string {
  return normalizeAuth0Origin(String(import.meta.env.VITE_AUTH0_COMPUTE_ORIGIN || DEFAULT_AUTH0_COMPUTE_ORIGIN));
}

function auth0RelayOrigin(): string {
  return normalizeAuth0Origin(String(import.meta.env.VITE_AUTH0_RELAY_ORIGIN || DEFAULT_AUTH0_RELAY_ORIGIN));
}

function auth0StatePrefixForOrigin(origin: string): string {
  if (origin === auth0PrimaryOrigin()) return "wallet";
  if (origin === auth0ComputeOrigin()) return "compute";
  if (origin === auth0RelayOrigin()) return "wallet";
  return "";
}

function auth0OriginForState(state: string): string {
  const prefix = String(state || "").split(".", 1)[0];
  if (prefix === "wallet") return auth0PrimaryOrigin();
  if (prefix === "compute") return auth0ComputeOrigin();
  if (!String(state || "").includes(".")) return auth0PrimaryOrigin();
  return "";
}

function canonicalHostedAuth0Origin(): string {
  if (typeof window === "undefined") return "";
  const currentOrigin = normalizeAuth0Origin(window.location.origin);
  const primaryOrigin = auth0PrimaryOrigin();
  const relayOrigin = auth0RelayOrigin();
  if (
    primaryOrigin &&
    (currentOrigin === LEGACY_AUTH0_RELAY_ORIGIN ||
      (relayOrigin && currentOrigin === relayOrigin))
  ) {
    return primaryOrigin;
  }
  return currentOrigin;
}

export function readAuth0LoggedOutMarker(): boolean {
  return readAuth0Storage("sessionStorage", AUTH0_LOGGED_OUT_KEY) === "1";
}

export function writeAuth0LoggedOutMarker(value: boolean): void {
  if (value) {
    writeAuth0Storage("sessionStorage", AUTH0_LOGGED_OUT_KEY, "1");
    return;
  }
  removeAuth0Storage("sessionStorage", AUTH0_LOGGED_OUT_KEY);
}

export function clearHostedAuth0BrowserState(): void {
  if (typeof window === "undefined") return;
  writePendingHostedAuth0(null);
  writeHostedAuth0Error("");
  writeAuth0SwitchFromEmail("");
  writeAuth0LoggedOutMarker(true);
  const clearAuth0Keys = (storage: Storage) => {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index) || "";
      if (key.startsWith("@@auth0spajs@@") || key.startsWith("auth0.")) {
        storage.removeItem(key);
      }
    }
  };
  try {
    clearAuth0Keys(window.localStorage);
    clearAuth0Keys(window.sessionStorage);
  } catch {
    // Storage can be blocked in hardened browser modes; browser/session logout still continues.
  }
  writeAuth0LoggedOutMarker(true);
}

function normalizeHostedAuthReturnTo(value: string): string {
  const fallback = `/ui${PRODUCT_HOME}`;
  const raw = String(value || "").trim();
  if (!raw) return fallback;

  const currentOrigin =
    typeof window !== "undefined"
      ? normalizeAuth0Origin(window.location.origin)
      : DEFAULT_AUTH0_PRIMARY_ORIGIN;
  const absolute = /^https?:\/\//i.test(raw);
  if (!absolute && !raw.startsWith("/")) return fallback;

  try {
    const url = new URL(raw, currentOrigin);
    if (url.origin !== currentOrigin) return fallback;

    const routePathname =
      url.pathname === "/ui"
        ? "/"
        : url.pathname.startsWith("/ui/")
          ? url.pathname.slice(3)
          : url.pathname;
    const normalizedRoutePathname = routePathname.replace(/\/+$/, "") || "/";
    if (
      normalizedRoutePathname === "/" ||
      normalizedRoutePathname === "/login" ||
      normalizedRoutePathname === "/auth/callback"
    ) {
      return fallback;
    }

    return `/ui${routePathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

function normalizeHostedAuthEmail(value: string): string {
  const email = String(value || "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "";
}

export function readAuth0SwitchFromEmail(): string {
  if (typeof window === "undefined") return "";
  return normalizeHostedAuthEmail(
    String(
      readAuth0Storage("sessionStorage", AUTH0_SWITCH_FROM_EMAIL_KEY) ||
        readAuth0Storage("localStorage", AUTH0_SWITCH_FROM_EMAIL_KEY) ||
        "",
    ),
  );
}

export function writeAuth0SwitchFromEmail(value: string): void {
  if (typeof window === "undefined") return;
  const email = normalizeHostedAuthEmail(value);
  if (email) {
    writeAuth0Storage("sessionStorage", AUTH0_SWITCH_FROM_EMAIL_KEY, email);
    writeAuth0Storage("localStorage", AUTH0_SWITCH_FROM_EMAIL_KEY, email);
    return;
  }
  removeAuth0Storage("sessionStorage", AUTH0_SWITCH_FROM_EMAIL_KEY);
  removeAuth0Storage("localStorage", AUTH0_SWITCH_FROM_EMAIL_KEY);
}

export function captureHostedAuthReturnTo(value?: string): void {
  if (typeof window === "undefined") return;
  const current = value ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;
  writeAuth0Storage(
    "sessionStorage",
    AUTH0_RETURN_TO_KEY,
    normalizeHostedAuthReturnTo(current),
  );
}

export function consumeHostedAuthReturnTo(): string {
  if (typeof window === "undefined") return `/ui${PRODUCT_HOME}`;
  const returnTo = readHostedAuthReturnTo();
  removeAuth0Storage("sessionStorage", AUTH0_RETURN_TO_KEY);
  return returnTo;
}

export function readHostedAuthReturnTo(): string {
  if (typeof window === "undefined") return `/ui${PRODUCT_HOME}`;
  return normalizeHostedAuthReturnTo(
    readAuth0Storage("sessionStorage", AUTH0_RETURN_TO_KEY).trim(),
  );
}

export function auth0RedirectURI(): string {
  if (typeof window === "undefined") return "";
  const callbackOrigin = canonicalHostedAuth0Origin();
  const configured = String(import.meta.env.VITE_AUTH0_REDIRECT_URI || "").trim();
  if (configured) {
    try {
      const url = new URL(configured, window.location.origin);
      const pathname = url.pathname.replace(/\/+$/, "");
      if (
        url.origin === callbackOrigin &&
        (pathname === "/ui/auth/callback" || pathname === "/auth/callback")
      ) {
        return url.toString();
      }
    } catch {
      // Fall back to the app-owned callback route when an env override is malformed.
    }
  }
  return `${callbackOrigin}/ui/auth/callback`;
}

export function relayHostedAuth0CallbackToPrimaryOrigin(): boolean {
  if (typeof window === "undefined") return false;
  const relayOrigin = auth0RelayOrigin();
  if (!relayOrigin || window.location.origin !== relayOrigin) return false;

  const hash = String(window.location.hash || "");
  if (!hash) return false;
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const state = String(params.get("state") || "").trim();
  const hasCallbackPayload = !!state && (!!params.get("id_token") || !!params.get("error"));
  if (!hasCallbackPayload) return false;

  const targetOrigin = auth0OriginForState(state);
  if (!targetOrigin || targetOrigin === relayOrigin) return false;
  const target = new URL("/ui/auth/callback", targetOrigin);
  target.hash = hash;
  window.location.replace(target.toString());
  return true;
}

export function relayHostedAuth0CodeCallbackToBackend(): boolean {
  if (typeof window === "undefined" || !isHostedAuth0CallbackRoute()) return false;
  const params = new URLSearchParams(String(window.location.search || ""));
  const state = String(params.get("state") || "").trim();
  const hasCodeFlowResult = !!String(params.get("code") || "").trim() ||
    !!String(params.get("error") || "").trim();
  if (!state || !hasCodeFlowResult) return false;

  const target = new URL(
    "/api/auth/hosted/auth0/callback",
    canonicalHostedAuth0Origin() || window.location.origin,
  );
  target.search = window.location.search;
  window.location.replace(target.toString());
  return true;
}

export function hostedAuth0StartURL(options?: HostedAuth0RedirectOptions): string {
  if (typeof window === "undefined") return "";
  const url = new URL("/api/auth/hosted/auth0", canonicalHostedAuth0Origin());
  const loginHint = normalizeHostedAuthEmail(String(options?.loginHint || ""));
  const expectedEmail = normalizeHostedAuthEmail(String(options?.expectedEmail || loginHint || ""));
  const disallowedEmail = normalizeHostedAuthEmail(String(options?.disallowedEmail || ""));
  const returnTo = normalizeHostedAuthReturnTo(String(options?.returnTo || readHostedAuthReturnTo()));
  if (options?.screenHint === "signup") {
    url.searchParams.set("screen_hint", "signup");
  }
  if (options?.connection === "google-oauth2") {
    url.searchParams.set("connection", options.connection);
  }
  if (expectedEmail) {
    url.searchParams.set("expected_email", expectedEmail);
  }
  if (loginHint) {
    url.searchParams.set("login_hint", loginHint);
  }
  if (disallowedEmail) {
    url.searchParams.set("disallowed_email", disallowedEmail);
  }
  url.searchParams.set("return_to", returnTo);
  return url.toString();
}

export function hostedAuthRouteOwnsCallback(): boolean {
  if (typeof window === "undefined") return false;
  const pathname = String(window.location.pathname || "").replace(/\/+$/, "") || "/";
  return pathname === "/ui/auth/callback" ||
    pathname === "/auth/callback" ||
    pathname === "/ui/login" ||
    pathname === "/login";
}

export function isHostedAuth0CallbackRoute(): boolean {
  if (typeof window === "undefined") return false;
  const pathname = String(window.location.pathname || "").replace(/\/+$/, "") || "/";
  return pathname === "/ui/auth/callback" || pathname === "/auth/callback";
}

export function isHostedAuth0RecoveryRoute(): boolean {
  if (typeof window === "undefined") return false;
  const pathname = String(window.location.pathname || "").replace(/\/+$/, "") || "/";
  if (pathname !== "/ui/login" && pathname !== "/login") return false;
  return new URLSearchParams(String(window.location.search || "")).has("auth_error");
}

function randomAuth0Value(length = 24): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) {
    out += chars[bytes[i] % chars.length];
  }
  return out;
}

function writePendingHostedAuth0(value: PendingHostedAuth0 | null) {
  if (typeof window === "undefined") return;
  if (!value) {
    removeAuth0Storage("sessionStorage", AUTH0_PENDING_KEY);
    clearHostedAuth0PendingCookie();
    return;
  }
  lastConsumedHostedAuth0Callback = "";
  removeAuth0Storage("sessionStorage", AUTH0_CONSUMED_CALLBACK_KEY);
  // The callback fails closed if neither session storage nor the hosted cookie is available.
  writeAuth0Storage("sessionStorage", AUTH0_PENDING_KEY, JSON.stringify(value));
}

function readHostedAuth0Cookie(name: string): string {
  if (typeof document === "undefined") return "";
  const prefix = `${name}=`;
  return String(document.cookie || "")
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length) || "";
}

function clearHostedAuth0PendingCookie() {
  if (typeof document === "undefined") return;
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  try {
    document.cookie = `${AUTH0_PENDING_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  } catch {
    // Hardened browser policies may make cookies read-only.
  }
}

function parsePendingHostedAuth0(raw: string): Partial<PendingHostedAuth0> {
  try {
    return JSON.parse(raw) as Partial<PendingHostedAuth0>;
  } catch {
    return JSON.parse(decodeURIComponent(raw)) as Partial<PendingHostedAuth0>;
  }
}

function readPendingHostedAuth0(): PendingHostedAuth0 | null {
  if (typeof window === "undefined") return null;
  try {
    let raw = readAuth0Storage("sessionStorage", AUTH0_PENDING_KEY);
    raw ||= readHostedAuth0Cookie(AUTH0_PENDING_COOKIE);
    if (!raw) return null;
    const parsed = parsePendingHostedAuth0(raw);
    const state = String(parsed.state || "").trim();
    const nonce = String(parsed.nonce || "").trim();
    const startedAt = Number(parsed.startedAt || 0);
    const expectedEmail = normalizeHostedAuthEmail(String(parsed.expectedEmail || ""));
    const disallowedEmail = normalizeHostedAuthEmail(String(parsed.disallowedEmail || ""));
    const returnTo = parsed.returnTo ? normalizeHostedAuthReturnTo(String(parsed.returnTo)) : "";
    const age = Date.now() - startedAt;
    if (
      !state ||
      !nonce ||
      !Number.isFinite(startedAt) ||
      (age >= 0 && age > AUTH0_PENDING_TTL_MS)
    ) {
      return null;
    }
    return {
      state,
      nonce,
      startedAt,
      ...(returnTo ? { returnTo } : {}),
      ...(expectedEmail ? { expectedEmail } : {}),
      ...(disallowedEmail ? { disallowedEmail } : {}),
    };
  } catch {
    return null;
  }
}

export function consumeHostedAuth0Error(): string {
  if (typeof window === "undefined") return "";
  const value = readAuth0Storage("sessionStorage", AUTH0_ERROR_KEY).trim();
  if (value) removeAuth0Storage("sessionStorage", AUTH0_ERROR_KEY);
  return value;
}

function writeHostedAuth0Error(message: string) {
  if (typeof window === "undefined") return;
  const value = String(message || "").trim();
  if (value) writeAuth0Storage("sessionStorage", AUTH0_ERROR_KEY, value);
  else removeAuth0Storage("sessionStorage", AUTH0_ERROR_KEY);
}

export function storeHostedAuth0Error(message: string) {
  writeHostedAuth0Error(message);
}

function hostedAuth0Prompt(options?: HostedAuth0RedirectOptions): string {
  const values = new Set<string>();
  const add = (raw: string) => {
    String(raw || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .forEach((value) => values.add(value));
  };
  if (options?.forceAccountSelection) {
    add("login select_account");
  }
  add(String(options?.prompt || ""));
  return Array.from(values).join(" ");
}

function buildHostedAuth0RedirectURL(options?: HostedAuth0RedirectOptions): string {
  const url = new URL(`https://${auth0Domain()}/authorize`);
  const statePrefix = auth0StatePrefixForOrigin(window.location.origin);
  const state = statePrefix ? `${statePrefix}.${randomAuth0Value(24)}` : randomAuth0Value(24);
  const nonce = randomAuth0Value(24);
  const loginHint = normalizeHostedAuthEmail(String(options?.loginHint || ""));
  const expectedEmail = normalizeHostedAuthEmail(String(options?.expectedEmail || loginHint || ""));
  const disallowedEmail = normalizeHostedAuthEmail(String(options?.disallowedEmail || ""));
  const returnTo = normalizeHostedAuthReturnTo(
    String(options?.returnTo || readHostedAuthReturnTo()),
  );
  writePendingHostedAuth0({
    state,
    nonce,
    startedAt: Date.now(),
    returnTo,
    ...(expectedEmail ? { expectedEmail } : {}),
    ...(disallowedEmail ? { disallowedEmail } : {}),
  });
  writeHostedAuth0Error("");
  url.searchParams.set("client_id", auth0ClientID());
  url.searchParams.set("redirect_uri", auth0RedirectURI());
  url.searchParams.set("response_type", "id_token");
  url.searchParams.set("scope", "openid profile email");
  url.searchParams.set("response_mode", "fragment");
  url.searchParams.set("state", state);
  url.searchParams.set("nonce", nonce);
  const prompt = hostedAuth0Prompt(options);
  if (prompt) {
    url.searchParams.set("prompt", prompt);
  }
  if (loginHint) {
    url.searchParams.set("login_hint", loginHint);
  }
  if (Number.isFinite(options?.maxAge)) {
    url.searchParams.set("max_age", String(Math.max(0, Math.floor(Number(options?.maxAge)))));
  }
  if (options?.screenHint === "signup") {
    url.searchParams.set("screen_hint", "signup");
  }
  if (options?.connection === "google-oauth2") {
    url.searchParams.set("connection", options.connection);
  }
  return url.toString();
}

export function startHostedAuth0Redirect(options?: HostedAuth0RedirectOptions) {
  if (typeof window === "undefined") throw new Error("Hosted sign-in requires a browser.");
  if (!auth0Enabled()) throw new Error("Hosted OAuth is not configured for this workspace yet.");
  const url = hostedAuth0StartURL(options);
  window.location.assign(url.toString());
}

export function prepareHostedAuth0RedirectURL(options?: HostedAuth0RedirectOptions): string {
  if (typeof window === "undefined") return "";
  if (!auth0Enabled()) throw new Error("Hosted OAuth is not configured for this workspace yet.");
  return buildHostedAuth0RedirectURL(options);
}

export function consumeHostedAuth0Callback(): { idToken?: string; error?: string; expectedEmail?: string; disallowedEmail?: string } | null {
  if (typeof window === "undefined") return null;
  const hash = String(window.location.hash || "").replace(/^#/, "");
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const idToken = String(params.get("id_token") || "").trim();
  const returnedState = String(params.get("state") || "").trim();
  const error = String(params.get("error") || "").trim();
  const errorDescription = String(params.get("error_description") || "").trim();
  if (!idToken && !error) return null;
  const callbackMarker = returnedState || `missing-state:${error ? "error" : "token"}`;
  if (lastConsumedHostedAuth0Callback === callbackMarker) return null;
  if (
    readAuth0Storage("sessionStorage", AUTH0_CONSUMED_CALLBACK_KEY) === callbackMarker
  ) {
    return null;
  }
  writeAuth0Storage("sessionStorage", AUTH0_CONSUMED_CALLBACK_KEY, callbackMarker);
  lastConsumedHostedAuth0Callback = callbackMarker;

  const pending = readPendingHostedAuth0();
  writePendingHostedAuth0(null);
  try {
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState({}, document.title, url.toString());
  } catch {
    // The one-time callback marker prevents replay if history mutation is unavailable.
  }

  if (!pending || pending.state !== returnedState) {
    const message = "Auth sign-in state mismatch. Please try again.";
    writeHostedAuth0Error(message);
    return { error: message };
  }
  if (pending.returnTo) captureHostedAuthReturnTo(pending.returnTo);
  if (error) {
    const message = errorDescription ? `${error}: ${errorDescription}` : error;
    writeHostedAuth0Error(message);
    return {
      error: message,
      expectedEmail: pending.expectedEmail,
      disallowedEmail: pending.disallowedEmail,
    };
  }
  return { idToken, expectedEmail: pending.expectedEmail, disallowedEmail: pending.disallowedEmail };
}

export function WalletAuth0Provider({ children }: { children: ReactNode }) {
  if (!auth0Enabled()) return <>{children}</>;

  return (
    <Auth0Provider
      domain={auth0Domain()}
      clientId={auth0ClientID()}
      cacheLocation="localstorage"
      useRefreshTokens
      skipRedirectCallback
      authorizationParams={{
        redirect_uri: auth0RedirectURI(),
      }}
      onRedirectCallback={(appState) => {
        if (typeof window === "undefined") return;
        const defaultTarget = `${window.location.origin}${consumeHostedAuthReturnTo()}`;
        const requested = String((appState as any)?.returnTo || "").trim();
        const target = requested || defaultTarget;
        const nextURL = target.startsWith("http") ? target : `${window.location.origin}${target.startsWith("/") ? target : `/${target}`}`;
        window.history.replaceState({}, document.title, nextURL);
      }}
    >
      {children}
    </Auth0Provider>
  );
}
